import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Table,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Typography,
  Progress,
  Button,
  Tabs,
  Tag,
  Divider,
} from "antd";
import { Bar } from "react-chartjs-2";
import {
  WarningOutlined,
  DollarOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import moment from "moment";
import { processDirectusLoan } from "../utils/loanUtils";

const predictRecovery = (loan) => {
  // Ensure numeric values
  const daysDelinquent = Number(loan.days_delinquent) || 0;
  const creditScore = Number(loan.borrower.credit_score) || 650;
  const outstandingBalance = Number(loan.outstanding_balance) || 0;

  // Feature Weights (Adjust based on your actual data patterns)
  const weights = {
    daysDelinquent: 0.35,
    creditScore: 0.25,
    paymentConsistency: 0.2,
    loanSize: 0.15,
    collateral: 0.05,
  };

  // Normalized features (0-1 scale)
  const features = {
    daysDelinquent: Math.min(daysDelinquent / 100, 1),
    creditScore: creditScore / 850,
    paymentConsistency: loan.payment_history?.consistency || 0.5,
    loanSize: 1 - Math.min(outstandingBalance / 100000, 1),
    collateral: loan.loan_product?.requires_collateral ? 1 : 0,
  };

  // Calculate probability (0-100 scale)
  let probability =
    100 *
    ((1 - features.daysDelinquent) * weights.daysDelinquent +
      features.creditScore * weights.creditScore +
      features.paymentConsistency * weights.paymentConsistency +
      features.loanSize * weights.loanSize +
      features.collateral * weights.collateral);

  // Apply non-linear decay for long delinquencies
  probability = Math.max(
    5,
    Math.min(95, probability * Math.exp(-0.015 * daysDelinquent))
  );

  return {
    probability: Number(probability.toFixed(1)),
    expectedRecovery: Number(
      (outstandingBalance * (probability / 100)).toFixed(2)
    ),
    riskCategory: getRiskCategory(probability),
  };
};

// Helper function for risk categorization
const getRiskCategory = (probability) => {
  if (probability >= 75) return "low";
  if (probability >= 50) return "medium";
  if (probability >= 25) return "high";
  return "very-high";
};

const DelinquencyReport = () => {
  const { company } = useContext(AppContext);
  const { Title, Text } = Typography;
  const { RangePicker } = DatePicker;
  const [rawData, setRawData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: [
      moment("2025-01-01"), // Start of dummy data range
      moment("2025-03-31"), // End of dummy data range
    ],
    loanProduct: "all",
    loanOfficer: "all",
  });

  const agingBuckets = [
    { label: "Current", days: 0 },
    { label: "1-30 Days", days: 30 },
    { label: "31-60 Days", days: 60 },
    { label: "61-90 Days", days: 90 },
    { label: "90+ Days", days: Infinity },
  ];

  const token = localStorage.getItem("token");

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/loans?fields=*,borrower.first_name,borrower.last_name,loan_product.*,loan_transactions.*&filter[loan_status]=7&filter[company]=${company.id}&limit=-1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Normalize and enrich loans for aging analysis
      const processed = response.data.data.map(processDirectusLoan);
      const enriched = processed.map((loan) => {
        const due = loan.next_payment_date || loan.due_date || loan.loan_enddate || loan.approval_date;
        const days = Number(loan.days_delinquent) || 0;
        return {
          ...loan,
          due_date: due,
          aging_bucket: calculateAgingBucket(days),
        };
      });
      setRawData(enriched);
    } catch (error) {
      console.error("Error fetching loans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, [company]);

  // Update aging bucket calculation
  const calculateAgingBucket = (daysDelinquent) => {
    const days = Number(daysDelinquent) || 0;
    if (days <= 0) return "Current";
    if (days <= 30) return "1-30 Days";
    if (days <= 60) return "31-60 Days";
    if (days <= 90) return "61-90 Days";
    return "90+ Days";
  };

  const chartData = {
    labels: agingBuckets.map((b) => b.label),
    datasets: [
      {
        label: "Number of Loans",
        data: agingBuckets.map(
          (bucket) =>
            rawData.filter(
              (loan) =>
                calculateAgingBucket(loan.days_delinquent) === bucket.label
            ).length || 0
        ),
        backgroundColor: [
          "#36a2eb",
          "#ff6384",
          "#4bc0c0",
          "#ff9f40",
          "#9966ff",
        ],
      },
      {
        label: "Amount Outstanding (ZMW)",
        data: agingBuckets.map(
          (bucket) =>
            rawData
              ?.filter((loan) => loan.aging_bucket === bucket.label)
              .reduce((sum, loan) => sum + loan.outstanding_balance, 0) || 0
        ),
        backgroundColor: [
          "#36a2eb55",
          "#ff638455",
          "#4bc0c055",
          "#ff9f4055",
          "#9966ff55",
        ],
      },
    ],
  };

  const columns = [
    {
      title: "Borrower",
      dataIndex: "borrower",
      render: (_, record) =>
        `${record.borrower.first_name} ${record.borrower.last_name}`,
    },
    {
      title: "Loan Product",
      dataIndex: ["loan_product", "loan_name"],
    },
    {
      title: "Outstanding Balance",
      dataIndex: "outstanding_balance",
      render: (value) => `ZMW ${Number(value).toLocaleString()}`,
      sorter: (a, b) => a.outstanding_balance - b.outstanding_balance,
    },
    {
      title: "Days Delinquent",
      dataIndex: "days_delinquent",
      render: (value) => (
        <Tag color={value > 60 ? "red" : value > 30 ? "orange" : "gold"}>
          {Math.max(0, value)} Days
        </Tag>
      ),
    },
    {
      title: "Aging Bucket",
      dataIndex: "aging_bucket",
      render: (value) => (
        <Tag
          color={
            value === "Current"
              ? "green"
              : value === "1-30 Days"
              ? "gold"
              : value === "31-60 Days"
              ? "orange"
              : "red"
          }
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Last Due Date",
      dataIndex: "due_date",
      render: (value) => moment(value).format("DD MMM YYYY"),
    },
    {
      title: "Recovery Probability",
      render: (_, record) => {
        const prediction = predictRecovery(record);
        return (
          <Progress
            percent={prediction.probability}
            status={
              prediction.riskCategory === "low"
                ? "success"
                : prediction.riskCategory === "medium"
                ? "active"
                : "exception"
            }
            format={() => `${prediction.probability}%`}
          />
        );
      },
    },
    {
      title: "Expected Recovery",
      render: (_, record) => {
        const prediction = predictRecovery(record);
        return (
          <Text
            strong
            style={{ color: prediction.probability > 50 ? "green" : "red" }}
          >
            ZMW {prediction.expectedRecovery.toLocaleString()}
          </Text>
        );
      },
    },
  ];

  const exportCSV = () => {
    try {
      if (!rawData.length) {
        alert("No data to export");
        return;
      }

      // Create CSV content
      const csvHeaders = [
        "Borrower Name",
        "Loan Product",
        "Loan Amount",
        "Outstanding Balance",
        "Days Delinquent",
        "Aging Bucket",
        "Last Due Date",
        "Recovery Probability",
        "Expected Recovery",
      ].join(",");

      const csvRows = rawData.map((loan) => {
        const prediction = predictRecovery(loan);
        return [
          `"${loan.borrower.first_name} ${loan.borrower.last_name}"`,
          `"${loan.loan_product.loan_name}"`,
          `ZMW ${Number(loan.amount).toLocaleString()}`,
          `ZMW ${Number(loan.outstanding_balance).toLocaleString()}`,
          loan.days_delinquent,
          `"${calculateAgingBucket(loan.days_delinquent)}"`,
          `"${moment(loan.due_date).format("YYYY-MM-DD")}"`,
          `${prediction.probability}%`,
          `ZMW ${prediction.expectedRecovery.toLocaleString()}`,
        ].join(",");
      });

      const csvContent = [csvHeaders, ...csvRows].join("\n");

      // Create downloadable file
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `delinquency-report-${moment().format(
        "YYYYMMDD-HHmmss"
      )}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Error exporting CSV");
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={3} style={{ marginBottom: 0 }}>
              <WarningOutlined /> Delinquency Report
            </Title>
            <Text type="secondary">Track and manage overdue loans</Text>
          </Col>
          <Col>
            <Button.Group>
              <Button onClick={fetchLoans}>Refresh</Button>
              {/* 
              <Button onClick={exportCSV} type="primary">
                Export CSV
              </Button> */}
            </Button.Group>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: "100%" }}
            />
          </Col>
          <Col span={8}>
            <Select
              placeholder="Loan Product"
              style={{ width: "100%" }}
              onChange={(value) =>
                setFilters({ ...filters, loanProduct: value })
              }
            >
              <Select.Option value="all">All Products</Select.Option>
              {[
                ...new Set(rawData.map((loan) => loan.loan_product.loan_name)),
              ].map((name) => (
                <Select.Option key={name} value={name}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Select
              placeholder="Loan Officer"
              style={{ width: "100%" }}
              onChange={(value) =>
                setFilters({ ...filters, loanOfficer: value })
              }
            >
              <Select.Option value="all">All Officers</Select.Option>
              {/* Populate with actual officers */}
              <Select.Option value="1">John Doe</Select.Option>
              <Select.Option value="2">Jane Smith</Select.Option>
            </Select>
          </Col>
        </Row>

        {/* Key Metrics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Delinquent Loans"
                value={rawData?.length || 0}
                prefix={<WarningOutlined />}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Exposure"
                value={
                  rawData?.reduce(
                    (sum, loan) => sum + loan.outstanding_balance,
                    0
                  ) || 0
                }
                prefix={<DollarOutlined />}
                suffix="ZMW"
                valueStyle={{ color: "#096dd9" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Average Days Delinquent"
                value={
                  rawData?.length
                    ? (
                        rawData.reduce(
                          (sum, loan) => sum + loan.days_delinquent,
                          0
                        ) / rawData.length
                      ).toFixed(1)
                    : 0
                }
                suffix="Days"
                prefix={<CalendarOutlined />}
                valueStyle={{ color: "#d48806" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Worst Case"
                value={
                  rawData?.length
                    ? Math.max(...rawData.map((loan) => loan.days_delinquent))
                    : 0
                }
                suffix="Days"
                prefix={<WarningOutlined />}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Charts */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card title="Delinquency Distribution" bordered={false}>
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: "top" },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const label = context.dataset.label || "";
                          const value = context.parsed.y || 0;
                          return `${label}: ${
                            context.dataset.label.includes("Amount")
                              ? `ZMW ${value}`
                              : value
                          }`;
                        },
                      },
                    },
                  },
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true },
                  },
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* Data Table */}
        <Card title="Delinquent Loan Details" bordered={false}>
          <Table
            columns={columns}
            dataSource={rawData}
            rowKey="id"
            loading={loading}
            scroll={{ x: true }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ margin: 0 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text strong>Disbursement Date:</Text>{" "}
                      {moment(record.disbursement_date).format("DD MMM YYYY")}
                    </Col>
                    <Col span={8}>
                      <Text strong>Original Amount:</Text> ZMW {record.amount}
                    </Col>
                    <Col span={8}>
                      <Text strong>Payments Received:</Text> ZMW{" "}
                      {record.paid_amount || 0}
                    </Col>
                  </Row>
                  <Divider />
                  <Button
                    type="link"
                    onClick={() => {
                      /* Add action */
                    }}
                  >
                    View Payment History
                  </Button>
                </div>
              ),
            }}
          />
        </Card>
      </Card>
    </div>
  );
};

export default DelinquencyReport;
